import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  Briefcase,
  Download,
  Upload,
  RotateCcw,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import PageLayout from '@components/layout/PageLayout';
import Button from '@components/common/Button';
import DetailPageHeader from '@components/common/DetailPageHeader';
import Badge from '@components/common/Badge';
import { RZTable } from '@components/common/table';
import { LoadingState } from '@components/common/loading';
import ErrorState from '@components/common/ErrorState';
import { UploadCorrectedFileModal } from '@components/features/import-jobs/UploadCorrectedFileModal';
import type { TableColumn } from '@app-types';
import type { ImportHistoryRecord, ImportErrorRecord, ImportStatus, ImportType } from '@app-types/import';
import { importService } from '@services';
import { showToast } from '@utils/toast';
import { formatMediumDateTime } from '@utils/dateUtils';
import styles from './ImportJobDetailPage.module.css';

function getJobName(record: ImportHistoryRecord): string {
  if (record.job_type === 'scheduled') {
    const type = record.import_type.charAt(0).toUpperCase() + record.import_type.slice(1);
    return `Import ${type} Job`;
  }
  return 'On Demand Import';
}

function getDisplayStatus(record: ImportHistoryRecord): ImportStatus {
  return record.is_completed ? 'Completed' : record.import_status;
}

function getResultBadgeVariant(status: ImportStatus): 'success' | 'warning' | 'error' | 'info' | 'pending' {
  switch (status) {
    case 'Processed':
      return 'info';
    case 'Processed with Errors':
      return 'warning';
    case 'Failed':
      return 'error';
    case 'Completed':
      return 'success';
    case 'Processing':
      return 'pending';
    default:
      return 'pending';
  }
}

function getJobTypeLabel(jobType: string): string {
  switch (jobType) {
    case 'scheduled':
      return 'Scheduled';
    case 'on_demand':
      return 'On Demand';
    default:
      return 'Manual Import';
  }
}

function formatDate(dateString: string): string {
  return formatMediumDateTime(dateString);
}

const ERROR_TABLE_COLUMNS: TableColumn[] = [
  { key: 'row_number', label: 'Row', sortable: false, defaultVisible: true },
  { key: 'field_name', label: 'Field Name', sortable: false, defaultVisible: true },
  { key: 'error_message', label: 'Error Description', sortable: false, defaultVisible: true },
];

const TERMINAL_STATUSES: ImportStatus[] = ['Processed', 'Processed with Errors', 'Failed'];

const ImportJobDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showConfirmComplete, setShowConfirmComplete] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const {
    data: job,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['import-job', id],
    queryFn: () => importService.getImportHistoryRecord(Number(id)),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const {
    data: errors = [],
    isLoading: errorsLoading,
  } = useQuery({
    queryKey: ['import-job-errors', id],
    queryFn: () => importService.getImportErrors(Number(id)),
    enabled: !!id && !!job && job.error_count > 0,
  });

  const completionMutation = useMutation({
    mutationFn: (isCompleted: boolean) => importService.setImportCompleted(Number(id), isCompleted),
    onSuccess: (_data, isCompleted) => {
      showToast.success(isCompleted ? 'Import marked as completed' : 'Import unmarked as completed');
      queryClient.invalidateQueries({ queryKey: ['import-job', id] });
      setShowConfirmComplete(false);
    },
    onError: () => {
      showToast.error('Failed to update import completion status');
      setShowConfirmComplete(false);
    },
  });

  const handleBack = () => navigate('/admin/data-management?tab=import-jobs');

  const handleDownloadSubset = async () => {
    try {
      await importService.downloadSubsetFile(Number(id));
      showToast.success('Subset file downloaded');
    } catch {
      showToast.error('Failed to download subset file');
    }
  };

  const handleUploadCorrected = () => {
    setShowUploadModal(true);
  };

  const handleRerunSuccess = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['import-job', id] });
    queryClient.invalidateQueries({ queryKey: ['import-job-errors', id] });
  }, [queryClient, id]);

  const handleTriggerRerun = async () => {
    try {
      await importService.resumeImport(Number(id));
      showToast.success('Import re-triggered successfully');
      queryClient.invalidateQueries({ queryKey: ['import-job', id] });
    } catch {
      showToast.error('Failed to re-trigger import');
    }
  };

  const handleMarkComplete = () => {
    setShowConfirmComplete(true);
  };

  const handleUnmarkComplete = () => {
    completionMutation.mutate(false);
  };

  const confirmMarkComplete = () => {
    completionMutation.mutate(true);
  };

  const renderErrorCell = (column: TableColumn, errorRecord: ImportErrorRecord) => {
    switch (column.key) {
      case 'row_number':
        return <span className={styles.rowNumber}>{errorRecord.row_number}</span>;

      case 'field_name':
        return <span className={styles.fieldName}>{errorRecord.field_name ?? '-'}</span>;

      case 'error_message':
        return <span>{errorRecord.error_message}</span>;

      default:
        return '-';
    }
  };

  if (isLoading) {
    return (
      <PageLayout title="Import Job Detail">
        <LoadingState message="Loading job details..." />
      </PageLayout>
    );
  }

  if (error || !job) {
    return (
      <PageLayout title="Import Job Detail">
        <ErrorState
          title="Job Not Found"
          message="The import job could not be found."
          action={
            <Button variant="primary" onClick={handleBack}>
              Back to Jobs
            </Button>
          }
        />
      </PageLayout>
    );
  }

  const hasErrors = job.error_count > 0;
  const isNotCompleted = !job.is_completed;
  const isFailed = job.import_status === 'Failed';
  const canMarkComplete = !job.is_completed && TERMINAL_STATUSES.includes(job.import_status);
  const canUnmarkComplete = job.is_completed;
  const displayStatus = getDisplayStatus(job);

  const actions = (
    <div className={styles.actions}>
      {hasErrors && isNotCompleted && job.job_type === 'scheduled' && (
        <>
          <Button variant="secondary" size="sm" onClick={handleDownloadSubset}>
            <Download size={16} />
            Generate Subset File
          </Button>
          <Button variant="secondary" size="sm" onClick={handleUploadCorrected}>
            <Upload size={16} />
            Upload Corrected File
          </Button>
        </>
      )}
      {isFailed && (
        <Button variant="secondary" size="sm" onClick={handleTriggerRerun}>
          <RotateCcw size={16} />
          Trigger Rerun
        </Button>
      )}
      {canUnmarkComplete && (
        <Button variant="secondary" size="sm" onClick={handleUnmarkComplete} disabled={completionMutation.isPending}>
          <XCircle size={16} />
          Unmark as Completed
        </Button>
      )}
      {canMarkComplete && (
        <Button variant="primary" size="sm" onClick={handleMarkComplete} disabled={completionMutation.isPending}>
          <CheckCircle size={16} />
          Mark as Completed
        </Button>
      )}
    </div>
  );

  return (
    <PageLayout title="Import Job Detail">
      <UploadCorrectedFileModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        importId={Number(id)}
        importType={job.import_type as ImportType}
        onSuccess={handleRerunSuccess}
      />

      <DetailPageHeader
        icon={<Briefcase size={24} />}
        title={getJobName(job)}
        subtitle={`Executed on ${formatDate(job.started_at)}`}
        backLabel="Back to Jobs"
        onBack={handleBack}
        actions={actions}
      />

      {showConfirmComplete && (
        <div className={styles.confirmOverlay}>
          <div className={styles.confirmDialog}>
            <h3>Confirm Completion</h3>
            <p>Are you sure you want to mark this import as completed?</p>
            <div className={styles.confirmActions}>
              <Button variant="secondary" size="sm" onClick={() => setShowConfirmComplete(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={confirmMarkComplete}
                disabled={completionMutation.isPending}
              >
                Confirm
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.content}>
        {/* Job Summary Section */}
        <div className={styles.summarySection}>
          <h2 className={styles.sectionTitle}>Job Summary</h2>
          <div className={styles.summaryGrid}>
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>Job Name</span>
              <span className={styles.summaryValue}>{getJobName(job)}</span>
            </div>
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>Date Executed</span>
              <span className={styles.summaryValue}>{formatDate(job.started_at)}</span>
            </div>
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>Executed By</span>
              <span className={styles.summaryValue}>{job.imported_by_name}</span>
            </div>
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>Job Type</span>
              <Badge variant={job.job_type === 'scheduled' ? 'purple' : 'default'}>
                {getJobTypeLabel(job.job_type)}
              </Badge>
            </div>
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>Result</span>
              <Badge variant={getResultBadgeVariant(displayStatus)}>
                {displayStatus}
              </Badge>
            </div>
          </div>
        </div>

        {/* Record Counts Section */}
        <div className={styles.countsSection}>
          <h2 className={styles.sectionTitle}>Record Counts</h2>
          <div className={styles.countsGrid}>
            <div className={styles.countCard}>
              <span className={styles.countValue}>{job.record_count}</span>
              <span className={styles.countLabel}>Total Records</span>
            </div>
            <div className={styles.countCardSuccess}>
              <span className={styles.countValue}>{job.success_count}</span>
              <span className={styles.countLabel}>Passed</span>
            </div>
            <div className={styles.countCardError}>
              <span className={styles.countValue}>{job.error_count}</span>
              <span className={styles.countLabel}>Failed</span>
            </div>
          </div>
        </div>

        {/* Failed Records Table */}
        {hasErrors && (
          <div className={styles.errorsSection}>
            <h2 className={styles.sectionTitle}>
              Failed Records
              <Badge variant="error" className={styles.errorBadge}>{job.error_count}</Badge>
            </h2>
            {errorsLoading ? (
              <LoadingState message="Loading error details..." />
            ) : (
              <div className={styles.errorsTableWrapper}>
                <RZTable
                  data={errors}
                  columns={ERROR_TABLE_COLUMNS}
                  getRowKey={(e) => e.id}
                  renderCell={renderErrorCell}
                  className={styles.errorsTable}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default ImportJobDetailPage;
